import * as React from "react";
import { Loading } from "../helpers/Loading";
import { FetchApi } from "../../FetchApi";
import { requestHelper } from "../helpers/requestHelper";
import { FormHelper, ReactiveForm } from "../helpers/ReactiveForm";
import { Modal } from "bootstrap";
import { Pagination } from "../helpers/Pagination";
import { useQuery } from "../helpers/useQuery";
import { setDebounce } from "../helpers/setDebounce";
import { CategoryModel, CategoryPage } from "./interfaces";

export const ListCategories = () => {
  const [categories, setCategories] = React.useState<CategoryPage | null>(null);
  const [category, setCategory] = React.useState<CategoryModel | null>(null);
  const [query, setQuery] = useQuery();
  const [parentCategory, setParentCategory] =
    React.useState<CategoryModel | null>(null);

  const retrieveCategories = (body = {}) => {
    const filters = parentCategory
      ? `parent_id:${parentCategory.id}`
      : "depth:0";

    FetchApi.get<CategoryPage>({
      path: "categories",
      body: { sorts: "-created_at", filters, ...body },
    }).then((response) => {
      setCategories(response);
    });
  };

  React.useEffect(() => {
    retrieveCategories(query);
  }, [query, parentCategory]);

  const toggleHandler = (category: CategoryModel) => {
    requestHelper({
      request: FetchApi.patch({
        path: `categories/${category.id}`,
        body: {
          is_disabled: !category.is_disabled,
        },
      }).then((response) => {
        retrieveCategories(query);
        return response;
      }),
    });
  };

  function addCategory(event: FormHelper) {
    const body = parentCategory
      ? { ...event.value, parent_id: parentCategory.id }
      : event.value;

    requestHelper({
      request: FetchApi.post({
        path: `categories`,
        body,
      }).then((m) => {
        retrieveCategories(query);
        return m;
      }),
      modal: "#addModal",
      form: event,
      reset: {
        success: true,
      },
    });
  }

  function updateCategory(event: FormHelper) {
    requestHelper({
      form: event,
      request: FetchApi.patch({
        path: `categories/${category?.id}`,
        body: event.value,
      }).then((m) => {
        retrieveCategories(query);
        return m;
      }),
      modal: "#editModal",
    });
  }

  const filterChange = (event: FormHelper) => {
    setDebounce(
      () => {
        setQuery(event.value, "merge");
      },
      "categories",
      800
    );
  };

  const viewChildren = (category: CategoryModel, event: React.MouseEvent) => {
    event.stopPropagation();
    setParentCategory(category);
    setQuery({}, "replace"); // Reset query when viewing children
  };

  const goBack = () => {
    setParentCategory(null);
    setQuery({}, "replace");
  };

  return (
    <>
      <button
        className="btn btn-primary btn-lg btn-floating"
        data-bs-toggle="modal"
        data-bs-target="#addModal"
      >
        <i className="fas fa-plus fa-fw" />
      </button>
      <div className="d-flex p-2 p-md-4" style={{ minHeight: "100vh" }}>
        <div className="my-auto w-100">
          <div className="d-flex align-items-center">
            {parentCategory && (
              <button className="btn btn-link me-2" onClick={goBack}>
                <i className="fas fa-arrow-right" />
              </button>
            )}
            <h2>
              {parentCategory
                ? `التصنيفات الفرعية - ${parentCategory.name}`
                : "التصنيفات"}
            </h2>
          </div>
          <div className="row">
            <div className="col-12">
              <ReactiveForm valueAs="json" onChange={filterChange}>
                <input type="hidden" name="page" value={1} />
                <div className="row">
                  <div className="col-md-4 col-sm-6 col-xl-3 my-3">
                    <div className="form-floating">
                      <input
                        type="text"
                        id="q"
                        name="q"
                        className="form-control"
                        placeholder=""
                        defaultValue={query["q"]}
                      />
                      <label htmlFor="q">بحث</label>
                    </div>
                  </div>
                </div>
              </ReactiveForm>
              <hr />
            </div>
            <Loading
              loading={categories}
              render={(categories) => {
                return (
                  <>
                    {categories.data.map((category) => (
                      <div
                        key={category.id}
                        className="col-md-4 col-sm-6 col-xl-3 my-3"
                      >
                        <div
                          className="card clickable h-100"
                          onClick={() => {
                            new Modal("#editModal").show();
                            setCategory(category);
                          }}
                        >
                          <div className="card-body d-flex flex-column">
                            <div className="d-flex">
                              {category.name}
                              <div className="flex-grow-1" />
                              <div className={"my-auto"}>
                                <input
                                  type="checkbox"
                                  className="slidebox inverted"
                                  readOnly={true}
                                  checked={category.is_disabled}
                                  onClickCapture={(event) => {
                                    event.nativeEvent.stopImmediatePropagation();
                                    toggleHandler(category);
                                  }}
                                />
                              </div>
                            </div>
                            {!parentCategory && (
                              <div className="mt-2">
                                <button
                                  className="btn btn-sm btn-outline-primary w-100"
                                  onClick={(e) => viewChildren(category, e)}
                                >
                                  <i className="fas fa-folder-open me-1" />
                                  عرض التصنيفات الفرعية
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <Pagination pagination={categories.meta} />
                  </>
                );
              }}
            />
          </div>
        </div>
      </div>
      <div
        className="modal fade"
        id="editModal"
        tabIndex={-1}
        aria-hidden="true"
      >
        <ReactiveForm
          onSubmit={updateCategory}
          valueAs={"json"}
          defaultValue={category || undefined}
          className="modal-dialog modal-dialog-centered"
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">
                تعديل تصنيف
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="form-floating">
                <input
                  type="text"
                  className="form-control"
                  defaultValue={category?.name}
                  name="name"
                  id="name"
                  placeholder=""
                />
                <label htmlFor="name">الاسم</label>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                الغاء
              </button>
              <button type="submit" className="btn btn-primary">
                حفظ
              </button>
            </div>
          </div>
        </ReactiveForm>
      </div>
      <div
        className="modal fade"
        id="addModal"
        tabIndex={-1}
        aria-hidden="true"
      >
        <ReactiveForm
          onSubmit={addCategory}
          valueAs={"json"}
          className="modal-dialog modal-dialog-centered"
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">
                اضافة تصنيف {parentCategory && `فرعي - ${parentCategory.name}`}
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="form-floating">
                <input
                  type="text"
                  className="form-control"
                  name="name"
                  id="name"
                  placeholder=""
                />
                <label htmlFor="name">الاسم</label>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                الغاء
              </button>
              <button type="submit" className="btn btn-primary">
                حفظ
              </button>
            </div>
          </div>
        </ReactiveForm>
      </div>
    </>
  );
};
